import { sendOmidMediaEvent } from '../../omid/omid';
import { reaction } from '../../utils/preact-utils';
import { RootStore } from '../../stores/root.store';
import { DebugLog } from '../../debug-log';
import {
  AdContainerExposure,
  AdContainerPosition
} from '../../../domain/ad-container.domain';

export interface OMIDGeometry extends AdContainerPosition {
  obstructions?: Array<AdContainerPosition>;
}

export type OMIDGeometryChange = {
  viewport: {
    width: number;
    height: number;
  };
  adView: {
    percentageInView: number;
    geometry: OMIDGeometry;
    onScreenGeometry: OMIDGeometry;
    reasons: string[];
  };
};

export class OmidEventsHandler {
  adContainerStore: RootStore['adContainerStore'];

  constructor({ adContainerStore }: RootStore) {
    this.adContainerStore = adContainerStore;

    this.initObservers();
  }

  initObservers(): void {
    reaction(
      () => this.adContainerStore.exposure.value,
      (exposure: AdContainerExposure) => {
        // This event is mandotory for OMID only when we are in accessMode "limited" such as sandbox iframe
        // To check if it can be removed, as limited access is only on Standard Banner web where we are mocking MRAID
        // And the geometry will never change
        // It could still be useful for IAB certification
        const omidGeometryChange = this.mapToOmidGeometry(exposure);
        sendOmidMediaEvent('geometryChange', omidGeometryChange);
        DebugLog.log('OmidEventsHandler: geometryChange', omidGeometryChange);
      },
      true
    );
  }

  mapToOmidGeometry({
    percentageInView,
    visibleRectangle,
    occlusionRectangles
  }: AdContainerExposure): OMIDGeometryChange {
    const {
      viewport,
      currentAdPosition,
      initialAdPosition
    } = RootStore.getInstance().adContainerStore.adContainerPort.getAdGeometryInfo();

    const adjustedVisibleRectangle = this.getAdjustedVisibleRectangle(
      viewport,
      visibleRectangle,
      currentAdPosition
    );

    const reasons = this.getOmidReasons(
      percentageInView,
      adjustedVisibleRectangle,
      viewport
    );

    return {
      viewport,
      adView: {
        percentageInView: percentageInView,
        geometry: {
          ...currentAdPosition,
          width: initialAdPosition.width,
          height: initialAdPosition.height
        },
        onScreenGeometry: {
          ...adjustedVisibleRectangle,
          obstructions: occlusionRectangles || []
        },
        reasons
      }
    };
  }

  getAdjustedVisibleRectangle(
    viewport: { width: number; height: number },
    visibleRectangle?: AdContainerPosition,
    currentAdPosition?: AdContainerPosition
  ): AdContainerPosition {
    if (visibleRectangle) {
      return visibleRectangle;
    }

    if (!currentAdPosition) {
      // Valeur par défaut si aucune info n'est disponible
      return { x: 0, y: 0, width: 0, height: 0 };
    }

    const adjustedRect: AdContainerPosition = { ...currentAdPosition };

    if (currentAdPosition.x < 0) {
      adjustedRect.width = currentAdPosition.width + currentAdPosition.x;
    } else if (currentAdPosition.x + currentAdPosition.width > viewport.width) {
      adjustedRect.width =
        currentAdPosition.x + currentAdPosition.width - viewport.width;
    }

    if (currentAdPosition.y < 0) {
      adjustedRect.height = currentAdPosition.height + currentAdPosition.y;
    } else if (
      currentAdPosition.y + currentAdPosition.height >
      viewport.height
    ) {
      adjustedRect.height =
        currentAdPosition.y + currentAdPosition.height - viewport.height;
    }

    return adjustedRect;
  }

  getOmidReasons(
    percentageInView: number,
    visibleRectangle: AdContainerPosition,
    viewport: { width: number; height: number }
  ): Array<string> {
    const reasons: string[] = [];
    if (percentageInView === 0) {
      reasons.push('hidden');
    }

    if (percentageInView < 100 && percentageInView !== 0) {
      reasons.push('obstructed');
    }

    if (
      visibleRectangle.x + visibleRectangle.width > viewport.width ||
      visibleRectangle.x < 0 ||
      visibleRectangle.y + visibleRectangle.height > viewport.height ||
      visibleRectangle.y < 0
    ) {
      reasons.push('viewport');
    }

    return reasons;
  }
}
